import { forwardRef, ReactNode } from 'react';
import { SortableContext } from '@dnd-kit/sortable';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { snakeToCamel } from '@rossum/api-client/utils';
import StatusChip from '@rossum/components/status/StatusChip';
import {
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { isEmbedded } from '../../constants/config';
import SortableWrapper, {
  DragHandleProps,
} from '../../decorators/SortableWrapper/SortableWrapper';
import { currentAnnotationQueueSelector } from '../../redux/modules/annotation/currentAnnotationQueueSelector';
import {
  DispatchEdit,
  EditDocumentConfig,
  hasBeenEdited,
  isEditable,
  isPartWithInaccessibleAnnotation,
  Page,
  Part,
} from '../document-edit-core';
import { QueueSelect } from '../queues/select-queue/QueueSelect';
import { EditedPage } from './EditedPage';
import { EditedPartActions } from './EditedPartActions';
import { FilenameWorkspaceQueueInfo } from './FilenameWorkspaceQueueInfo';
import { UiStateTuple } from './uiState';

const SortablePages = ({
  dragAndDropEnabled,
  pages,
  render,
}: {
  dragAndDropEnabled: boolean;
  pages: Page[];
  render: (
    page: Page,
    index: number,
    dragHandleProps?: DragHandleProps
  ) => ReactNode;
}) => {
  if (dragAndDropEnabled) {
    return (
      <SortableContext items={pages.map(p => ({ id: String(p.pageNumber) }))}>
        {pages.map((page, i) => (
          <SortableWrapper
            key={page.pageNumber}
            id={String(page.pageNumber)}
            render={dragHandleProps => render(page, i, dragHandleProps)}
          />
        ))}
      </SortableContext>
    );
  }

  return <>{pages.map((page, i) => render(page, i))}</>;
};

export const EditedPart = forwardRef<
  HTMLDivElement,
  {
    part: Part;
    index: number;
    dispatchEdit: DispatchEdit;
    isFollowedByEditablePart: boolean;
    uiState: UiStateTuple;
    config: EditDocumentConfig;
    suggestedSplitsActive: boolean;
  }
>(
  (
    {
      part,
      dispatchEdit,
      index,
      isFollowedByEditablePart,
      uiState: [uiState, setUiState],
      config,
      suggestedSplitsActive,
    },
    ref
  ) => {
    const intl = useIntl();
    const { pages } = part;
    const currentQueue = useSelector(currentAnnotationQueueSelector);

    const canBeEditedByUser = isEditable(part);
    const editable = canBeEditedByUser && !uiState.confirmInProgress;

    const edited = hasBeenEdited(part, config);

    const editValues = currentQueue?.settings.uiEditValues ?? [];

    const targetQueueSelectionEnabled =
      currentQueue?.settings.uiEditTargetQueueSelectionEnabled ?? true;

    const partEditValues =
      part.annotationType !== 'inaccessible' ? part.editValues : {};

    return (
      <Stack
        spacing={2}
        sx={{
          scrollMargin: '32px',
        }}
        ref={ref}
        data-cy="edit-part-container"
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {/* lineHeight ensures that showing a chip won't increase the height of the Stack  */}
              <Typography
                variant="h6"
                sx={{ lineHeight: 1.5 }}
                data-cy="edit-part-title"
              >
                {intl.formatMessage(
                  { id: 'containers.editDocument.part.title' },
                  {
                    partNumber: index + 1,
                  }
                )}
              </Typography>
              {part.annotationType === 'accessible' &&
                // TODO remove this in type definition, we won't receive purged annotations
                part.annotation.status !== 'purged' &&
                // A started child normally reports 'reviewing', which tells the
                // user nothing — but it keeps a meaningful status when the
                // backend preserves one (e.g. a confirmed child stays confirmed
                // while it is being edited), and that is worth showing.
                !(
                  part.annotation.started &&
                  part.annotation.status === 'reviewing'
                ) && (
                  <StatusChip status={snakeToCamel(part.annotation.status)} />
                )}
              {!canBeEditedByUser ? (
                <Chip
                  label={intl.formatMessage({
                    id: 'containers.editDocument.part.editDisabledChip',
                  })}
                  sx={{ px: 0.25 }}
                  color="warning"
                />
              ) : suggestedSplitsActive ? (
                <Chip
                  label={intl.formatMessage({
                    id: 'containers.editDocument.part.splitSuggestionChip',
                  })}
                  sx={{ px: 0.25 }}
                  color="info"
                />
              ) : edited ? (
                <Chip
                  label={intl.formatMessage({
                    id: 'containers.editDocument.part.editedChip',
                  })}
                  sx={{ px: 0.25 }}
                  color="info"
                />
              ) : null}
            </Stack>
            <FilenameWorkspaceQueueInfo
              filename={
                // Don't show filename for edited parts,
                // since the filename will change after confirmation,
                // so it could be confusing.
                part.annotationType !== 'none' && !edited
                  ? part.annotation.filename
                  : null
              }
              queueUrl={
                part.annotationType !== 'inaccessible'
                  ? part.targetQueueUrl
                  : null
              }
            />
          </Stack>
          {canBeEditedByUser ? (
            <EditedPartActions part={part} dispatchEdit={dispatchEdit} />
          ) : null}
        </Stack>
        {isEmbedded() ? null : isPartWithInaccessibleAnnotation(part) ? null : (
          <Stack spacing={2}>
            {targetQueueSelectionEnabled && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  flexShrink: 0,
                }}
              >
                <QueueSelect
                  value={part.targetQueueUrl}
                  onChange={queueUrl =>
                    queueUrl
                      ? dispatchEdit({
                          type: 'SET_TARGET_QUEUE',
                          partIndex: index,
                          queueUrl,
                        })
                      : null
                  }
                  getOptionLabel={option => option.name}
                  forceOpenWorkspaces={false}
                  hideDisabledQueues
                  disableClearable
                  disabled={!editable}
                  sx={{ width: 300 }}
                />
              </Stack>
            )}
            {editValues.length > 0 && (
              <Stack spacing={2}>
                {editValues.map(editValue => (
                  <Stack
                    key={editValue.id}
                    direction="row"
                    sx={{ justifyContent: 'flex-end' }}
                  >
                    {editValue.dataType === 'string' ? (
                      <TextField
                        size="small"
                        label={editValue.label}
                        required={editValue.required}
                        value={partEditValues[editValue.id] ?? ''}
                        onChange={e =>
                          dispatchEdit({
                            type: 'SET_EDIT_VALUE',
                            partIndex: index,
                            valueId: editValue.id,
                            value: e.target.value,
                          })
                        }
                        disabled={!editable}
                        sx={{ width: 300 }}
                      />
                    ) : (
                      <TextField
                        select
                        size="small"
                        label={editValue.label}
                        required={editValue.required}
                        value={partEditValues[editValue.id] ?? ''}
                        onChange={e =>
                          dispatchEdit({
                            type: 'SET_EDIT_VALUE',
                            partIndex: index,
                            valueId: editValue.id,
                            value: e.target.value,
                          })
                        }
                        disabled={!editable}
                        sx={{ width: 300 }}
                      >
                        {editValue.enumOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}
        <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
          <SortablePages
            pages={pages}
            dragAndDropEnabled={editable}
            render={(page, i, dragHandleProps) => (
              <EditedPage
                page={page}
                followingPageNumber={pages[i + 1]?.pageNumber}
                isLastPage={i === pages.length - 1}
                isFollowedByEditablePart={isFollowedByEditablePart}
                partIndex={index}
                dragHandleAttributes={dragHandleProps?.attributes}
                dragHandleListeners={dragHandleProps?.listeners}
                dispatchEdit={dispatchEdit}
                uiState={[uiState, setUiState]}
                mode={
                  page.pageNumber === uiState.draggedPage?.number
                    ? 'placeholder-for-dragged-page'
                    : 'not-dragged-page'
                }
                editable={editable}
                config={config}
              />
            )}
          />
        </Stack>
      </Stack>
    );
  }
);

EditedPart.displayName = 'EditedPart';
